<template>
  <div>
    <router-view />
    <iframe
      id="ulaIframe"
      title="ulaIframe"
      name="ulaIframe"
      sandbox="allow-scripts allow-same-origin allow-storage-access-by-user-activation"
      :src="ulaURL"
      :style="'right: 0; position: absolute; width: 360px; display: none'"
    ></iframe>
  </div>
</template>

<script lang="ts" setup>
import { UlaEvent } from '@/types/ula';
import { useAuthentication } from '@/composables/auth/useAuthentication';
import { useConfig } from '@/composables/useConfig';
import { onMounted } from 'vue';
import { useWebSocket } from '@/composables/useWebSocket';
import { useLogger } from '@/composables/useLogger';
import { useAuthorization } from '@/composables/auth/useAuthorization';

// Composables
const { isLogged, initLoggedUser, logout, refreshLogin } = useAuthentication();
const { hasLoggedUserPermission } = useAuthorization();
const { createSocket, getSocket } = useWebSocket();
const { setConfig, getConfig } = useConfig();
const { logger } = useLogger();

// Derived values
const socket = getSocket();
const ulaURL = getConfig().ULA_URL;

// Handle ULA messages
const handleUlaMessage = async (event: MessageEvent<UlaEvent>): Promise<void> => {
  // Validate message origin
  if (event.origin !== getConfig().ULA_URL) {
    return;
  }

  logger.debug(`📨 message from ULA iframe: ${event.data.name}`, event.data);
  switch (event.data.name) {
    case 'ula-user-identity': {
      await handleUserIdentity(event.data);
      break;
    }
    case 'ula-logout': {
      await handleLogout();
      break;
    }
    case 'ula-id-token':
      // handled as promise based communication in useUla composable
      break;
    default: {
      logger.debug('Unknown message from ULA iframe: ', event);
    }
  }
};

// Handle user identity event
const handleUserIdentity = async (userData: UlaEvent) => {
  // Set tenant if not already set
  if (!getConfig().TENANT) {
    setConfig({
      ...getConfig(),
      TENANT: userData.user.tenant,
    });
  }

  // Initialize socket if needed
  if (!socket) {
    createSocket();
  }

  // Handle login/refresh based on current state
  if (isLogged.value) {
    await refreshLogin(userData.user);
  } else {
    await initLoggedUser(userData.user);
  }

  // Check permissions
  if (!hasLoggedUserPermission('ERP_APP', 'LOGIN')) {
    await logout(false);
  }
};

// Handle logout event
const handleLogout = async () => {
  logger.info("'ula-logout' message from ULA iframe, logging out...");
  await logout(false);
};

// Setup message listener
onMounted(() => {
  globalThis.addEventListener('message', handleUlaMessage);
});
</script>
